import { Spinner } from "../components";
import Icon from "../components/Icon";
import { useState, useRef, useEffect, useId } from "react";
import { apiFetch } from "../api";

/**
 * AddressAutocomplete
 * Campo de busca com autocomplete do Google Places.
 * Ao selecionar uma sugestão, chama onSelect com { rua, numero, bairro, cidade }.
 *
 * Props:
 *   value      — string atual do campo
 *   onChange   — chamado ao digitar (string)
 *   onSelect   — chamado ao selecionar sugestão ({ rua, numero, bairro, cidade })
 *   placeholder
 *   label
 */
export default function AddressAutocomplete({ value, onChange, onSelect, placeholder = "Av. Paulista", label, error }) {
  const inputId = useId();
  const suggestionsId = `${inputId}-suggestions`;
  const [sugestoes, setSugestoes] = useState([]);
  const [aberto, setAberto] = useState(false);
  const [loadingSug, setLoadingSug] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  // Fecha dropdown ao clicar fora
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setAberto(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const buscar = (texto) => {
    clearTimeout(debounceRef.current);
    if (!texto || texto.length < 3) { setSugestoes([]); setAberto(false); return; }
    debounceRef.current = setTimeout(async () => {
      setLoadingSug(true);
      try {
        const res = await apiFetch(`/places/autocomplete?q=${encodeURIComponent(texto)}`);
        setSugestoes(res);
        setAberto(res.length > 0);
        setHighlighted(-1);
      } catch { setSugestoes([]); }
      finally { setLoadingSug(false); }
    }, 350);
  };

  const selecionar = async (prediction) => {
    setAberto(false);
    setSugestoes([]);
    // Mostra o endereço formatado no campo enquanto carrega os detalhes
    onChange(prediction.structured_formatting?.main_text || prediction.description);
    try {
      const detalhes = await apiFetch(`/places/details/${prediction.place_id}`);
      onSelect(detalhes);
    } catch { /* se falhar, o usuário preenche manualmente */ }
  };

  const handleKeyDown = (e) => {
    if (!aberto || sugestoes.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlighted(h => Math.min(h + 1, sugestoes.length - 1)); }
    if (e.key === "ArrowUp")   { e.preventDefault(); setHighlighted(h => Math.max(h - 1, 0)); }
    if (e.key === "Enter" && highlighted >= 0) { e.preventDefault(); selecionar(sugestoes[highlighted]); }
    if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); setAberto(false); }
  };

  return (
    <div ref={wrapperRef} style={{ position: "relative", minWidth: 0 }}>
      {label && (
        <label htmlFor={inputId} style={{ fontSize: '0.9375rem', color: error ? "var(--danger)" : "var(--muted)", fontWeight: 500, display: "block", marginBottom: 6 }}>
          {label}
        </label>
      )}
      <div style={{ position: "relative" }}>
        <input
          type="text" id={inputId} role="combobox" aria-autocomplete="list" aria-expanded={aberto && sugestoes.length > 0}
          aria-controls={aberto ? suggestionsId : undefined} aria-activedescendant={aberto && highlighted >= 0 ? `${suggestionsId}-${highlighted}` : undefined}
          aria-invalid={error ? true : undefined} aria-describedby={error ? `${inputId}-error` : undefined}
          value={value}
          onChange={e => { onChange(e.target.value); buscar(e.target.value); }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoComplete="off"
          style={{
            width: "100%", minHeight: 44, maxWidth: "100%", background: "var(--s2)", border: `1px solid ${error ? "var(--danger)" : "var(--border)"}`,
            borderRadius: 8, padding: "10px 36px 10px 12px", color: "var(--text)",
            fontSize: '1rem', outline: "none", boxSizing: "border-box", transition: "border-color 0.2s",
            fontFamily: "inherit"
          }}
          onFocus={e => e.target.style.borderColor = error ? "var(--danger)" : "var(--accent)"}
          onBlur={e => e.target.style.borderColor = error ? "var(--danger)" : "var(--border)"}
        />
        <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", fontSize: '1rem', color: "var(--muted)", pointerEvents: "none" }}>
          {loadingSug ? <Spinner /> : <Icon name="search" size={16} />}
        </span>
      </div>
      {error && <span id={`${inputId}-error`} role="alert" style={{ fontSize: '0.875rem', color: "var(--danger)", marginTop: 4, display: "block" }}><Icon name="warning" /> {error}</span>}

      {/* Dropdown de sugestões */}
      {aberto && sugestoes.length > 0 && (
        <div id={suggestionsId} role="listbox" aria-label="Sugestões de endereço" style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 200,
          background: "var(--s1)", border: "1px solid var(--border)", borderRadius: 10,
          boxShadow: "0 8px 24px rgba(0,0,0,0.4)", overflow: "visible"
        }}>
          {sugestoes.map((s, i) => (
            <div
              key={s.place_id} id={`${suggestionsId}-${i}`} role="option" aria-selected={highlighted === i}
              onMouseDown={() => selecionar(s)}
              style={{
                minHeight: 44, padding: "10px 14px", cursor: "pointer", fontSize: '0.9375rem',
                background: highlighted === i ? "var(--s2)" : "transparent",
                borderBottom: i < sugestoes.length - 1 ? "1px solid var(--border)" : "none",
                transition: "background 0.1s"
              }}
              onMouseEnter={() => setHighlighted(i)}
            >
              <div style={{ fontWeight: 500, color: "var(--text)" }}>
                {s.structured_formatting?.main_text || s.description}
              </div>
              <div style={{ fontSize: '0.8125rem', color: "var(--muted)", marginTop: 2 }}>
                {s.structured_formatting?.secondary_text || ""}
              </div>
            </div>
          ))}
          <div style={{ padding: "6px 14px", fontSize: '0.8125rem', color: "var(--muted)", textAlign: "right", borderTop: "1px solid var(--border)" }}>
            powered by Google
          </div>
        </div>
      )}
    </div>
  );
}